import "./index.css"

import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Link } from '@design-system/link';
import classNames from 'classnames';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@design-system/button';
import { useDebounce } from '@design-system/hooks';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

const OVERFLOW_ITEM_CLASS_NAME = 'cds-breadcrumbs__overflow-item';
const BREADCRUMBS_ITEM_CLASS_NAME = 'cds-breadcrumbs__item';
const BLUR_DELAY = 500;

const Overflow = ({ overflowBtn, extendedItems, linkComponent: LinkComponent = Link, }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ariaLabel = overflowBtn['aria-label'];
    const handleClick = (e) => {
        e.preventDefault();
        setIsOpen(!isOpen);
    };
    const timer = useRef();
    const handleBlur = () => {
        timer.current = setTimeout(() => {
            setIsOpen(false);
        }, BLUR_DELAY);
    };
    const handleFocus = () => {
        if (timer.current)
            clearTimeout(timer.current);
    };
    const handleEsc = (event) => {
        if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };
    useEffect(() => {
        window.addEventListener('keyup', handleEsc);
        return () => {
            window.removeEventListener('keyup', handleEsc);
        };
    }, []);
    return (jsxs("li", Object.assign({ "aria-hidden": !extendedItems.length, className: classNames(OVERFLOW_ITEM_CLASS_NAME, {
            'cds-breadcrumbs__overflow-item--hidden': !extendedItems.length,
        }) }, { children: [jsx(Button, Object.assign({ onClick: handleClick, onBlur: handleBlur, variant: "subtle", size: "small", "aria-label": ariaLabel, "aria-expanded": isOpen, className: classNames({ active: isOpen }) }, (!extendedItems.length ? { tabIndex: -1 } : null), { children: overflowBtn.children })), isOpen ? (jsx("div", Object.assign({ className: "cds-breadcrumbs__drop" }, { children: jsx("ul", Object.assign({ className: "cds-breadcrumbs__drop-list" }, { children: extendedItems.map((moreItem) => (jsx("li", Object.assign({ className: "cds-breadcrumbs__drop-item" }, { children: jsx(LinkComponent, Object.assign({ className: "cds-breadcrumbs__drop-link" }, moreItem, { size: "small", variant: "subtle", onFocus: handleFocus, onBlur: handleBlur }, { children: moreItem.children })) }), moreItem.href))) })) }))) : null] })));
};

const calculateItemsVisibility = (listItems, overflowWidth, navWidth, hideLastItem) => {
    const firstItemWidth = listItems[0].width;
    const lastItemFactor = hideLastItem ? 1 : 0;
    const lastItemWidth = listItems[Math.max(0, listItems.length - (1 + lastItemFactor))].width;
    const initialWidth = overflowWidth + firstItemWidth + lastItemWidth;
    const data = [...listItems];
    let sum = initialWidth;
    const penultimateIndex = data.length - (2 + lastItemFactor);
    for (let i = penultimateIndex; i > 0; i -= 1) {
        sum += data[i].width;
        data[i].isVisible = sum < navWidth;
    }
    data[Math.max(0, data.length - (1 + lastItemFactor))].isEclipsed =
        initialWidth > navWidth;
    return data;
};

const splitVisibleItems = (data) => {
    const [visibleArr, hiddenArr] = data.reduce((prev, current) => {
        const [visible, hidden] = prev;
        const { width, isVisible } = current, item = __rest(current, ["width", "isVisible"]);
        if (width && isVisible) {
            visible.push(item);
        }
        else {
            hidden.push(item);
        }
        return [visible, hidden];
    }, [[], []]);
    return [visibleArr, hiddenArr];
};

function useOverflow(items, hideLastItem, navRef) {
    const [extendedItems, setExtendedItems] = useState(items
        .filter((_item, idx) => (hideLastItem ? idx !== items.length - 1 : true))
        .map((item) => (Object.assign(Object.assign({}, item), { isVisible: true, width: 0 }))));
    const [visibleItems, setVisibleItems] = useState(items.filter((_item, idx) => hideLastItem ? idx !== items.length - 1 : true));
    const [hiddenItems, setHiddenItems] = useState([]);
    const [navWidth, setNavWidth] = useState(0);
    const [overflowWidth, setOverflowWidth] = useState(0);
    const debouncedNavWidth = useDebounce(navWidth, 300);
    useEffect(() => {
        if (!(navRef === null || navRef === void 0 ? void 0 : navRef.current))
            return;
        const overflowElement = Array.from(navRef.current.children).find((el) => el.className.includes(OVERFLOW_ITEM_CLASS_NAME));
        if (overflowElement) {
            setOverflowWidth(overflowElement.clientWidth);
        }
    }, [setOverflowWidth, navRef]);
    useEffect(() => {
        if (!(navRef === null || navRef === void 0 ? void 0 : navRef.current))
            return;
        const breadcrumbsItemsEl = Array.from(navRef.current.children).filter((el) => el.className.includes(BREADCRUMBS_ITEM_CLASS_NAME));
        setExtendedItems((prevVal) => prevVal.map((item, idx) => {
            var _a;
            return (Object.assign(Object.assign({}, item), { width: ((_a = breadcrumbsItemsEl === null || breadcrumbsItemsEl === void 0 ? void 0 : breadcrumbsItemsEl[idx]) === null || _a === void 0 ? void 0 : _a.clientWidth) || 0 }));
        }));
    }, [setExtendedItems, navRef]);
    const handleResize = useCallback(() => {
        if (!navRef.current)
            return;
        setNavWidth(navRef.current.getBoundingClientRect().width);
    }, [navRef]);
    const handleOverflow = useCallback(() => {
        if (extendedItems[0].width === 0)
            return;
        const data = calculateItemsVisibility(extendedItems, overflowWidth, navWidth, hideLastItem);
        const [visible, hidden] = splitVisibleItems(data);
        setVisibleItems(visible);
        setHiddenItems(hidden);
        setExtendedItems(data);
    }, [extendedItems, navWidth, overflowWidth]);
    useEffect(handleResize, [handleResize]);
    useEffect(handleOverflow, [debouncedNavWidth]);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);
    return { visibleItems, hiddenItems };
}

const noScriptStyles = `
  .cds-breadcrumbs__list {
    flex-wrap: wrap;
  }
`;
const Breadcrumbs = ({ className, navigationName, items, linkComponent, overflowBtn, hideLastItem = false, }) => {
    const LinkComponent = linkComponent || Link;
    const navRef = useRef(null);
    const { visibleItems, hiddenItems } = useOverflow(items, hideLastItem, navRef);
    const [firstItem, ...arrWithoutFirstItem] = visibleItems;
    return (jsxs(Fragment, { children: [jsx("nav", Object.assign({ "aria-label": navigationName, className: classNames('cds-breadcrumbs', className) }, { children: jsxs("ol", Object.assign({ ref: navRef, className: "cds-breadcrumbs__list" }, { children: [jsx("li", Object.assign({ className: BREADCRUMBS_ITEM_CLASS_NAME }, { children: jsx(LinkComponent, Object.assign({ className: "cds-breadcrumbs__link", size: "small", variant: "subtle" }, firstItem)) })), visibleItems.length > 1 && overflowBtn ? (jsx(Overflow, { extendedItems: hiddenItems, linkComponent: linkComponent, overflowBtn: overflowBtn })) : null, arrWithoutFirstItem.map((_a, idx, list) => {
                            var { isEclipsed } = _a, item = __rest(_a, ["isEclipsed"]);
                            return (jsx("li", Object.assign({ className: classNames(BREADCRUMBS_ITEM_CLASS_NAME, {
                                    [`${BREADCRUMBS_ITEM_CLASS_NAME}--ellipsis`]: isEclipsed,
                                }) }, { children: jsx(LinkComponent, Object.assign({ className: "cds-breadcrumbs__link", size: "small", variant: "subtle" }, (idx === list.length - 1 && !hideLastItem
                                    ? { 'aria-current': 'page' }
                                    : {}), item)) }), item.href));
                        })] })) })), jsx("noscript", { children: jsx("style", { children: noScriptStyles }) })] }));
};

export { Breadcrumbs };
